// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState } from 'react';
import { BackButton } from '../components/common';
import { useNavigate } from 'react-router-dom';

const CookiePolicyPage = ({ onBack }) => {
  const navigate = useNavigate();
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always enabled
    functional: true,
    analytics: false,
    marketing: false
  });

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_preferences', JSON.stringify(cookieSettings));
    alert('✅ Cookie preferences saved successfully!');
  };

  const renderHeader = () => {
    if (onBack) {
      return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <BackButton onClick={onBack} label="← More" className="mb-2" />
            <h1 className="text-2xl font-bold text-gray-900">Cookie Policy</h1>
          </div>
        </header>
      );
    }

    return (
      <nav className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span>
              </span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-sm sm:text-base text-gray-700 hover:text-blue-600 font-semibold transition whitespace-nowrap"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderHeader()}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          {!onBack && <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>}
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> 11 February 2026
          </p>

          <div className="space-y-8 text-gray-700">
            {/* What Are Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p className="mb-4">
                Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our service.
              </p>
            </section>

            {/* Cookie Settings Panel */}
            <section className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">🍪 Manage Your Cookie Preferences</h2>
              
              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">Necessary Cookies</h3>
                      <p className="text-sm text-gray-600">Required for the website to function</p>
                    </div>
                    <div className="px-4 py-2 bg-gray-200 rounded-lg text-gray-600 font-semibold">
                      Always Active
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    These cookies are essential for login, security, and basic functionality.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">Functional Cookies</h3>
                      <p className="text-sm text-gray-600">Remember your preferences</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookieSettings.functional}
                        onChange={(e) => setCookieSettings({...cookieSettings, functional: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Remember your language, region, shopping lists, and display preferences.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">Analytics Cookies</h3>
                      <p className="text-sm text-gray-600">Help us improve our service</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookieSettings.analytics}
                        onChange={(e) => setCookieSettings({...cookieSettings, analytics: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Anonymous usage data to understand how visitors use our website.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">Marketing Cookies</h3>
                      <p className="text-sm text-gray-600">Personalized recommendations</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookieSettings.marketing}
                        onChange={(e) => setCookieSettings({...cookieSettings, marketing: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Show you relevant deals and offers based on your shopping habits.
                  </p>
                </div>
              </div>

              <button
                onClick={handleSavePreferences}
                className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg hover:shadow-lg transition"
              >
                💾 Save Preferences
              </button>
            </section>

            {/* Detailed Cookie Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Cookies We Use</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Cookie Name</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Purpose</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Duration</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">access_token</td>
                      <td className="border border-gray-300 px-4 py-2">User authentication (httpOnly)</td>
                      <td className="border border-gray-300 px-4 py-2">24 hours</td>
                      <td className="border border-gray-300 px-4 py-2">Necessary</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">refresh_token</td>
                      <td className="border border-gray-300 px-4 py-2">Session renewal (httpOnly)</td>
                      <td className="border border-gray-300 px-4 py-2">7 days</td>
                      <td className="border border-gray-300 px-4 py-2">Necessary</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">admin_token</td>
                      <td className="border border-gray-300 px-4 py-2">Admin authentication (httpOnly)</td>
                      <td className="border border-gray-300 px-4 py-2">24 hours</td>
                      <td className="border border-gray-300 px-4 py-2">Necessary</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">cookie_preferences</td>
                      <td className="border border-gray-300 px-4 py-2">Your cookie consent choices</td>
                      <td className="border border-gray-300 px-4 py-2">1 year</td>
                      <td className="border border-gray-300 px-4 py-2">Necessary</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">pricepal-accessibility</td>
                      <td className="border border-gray-300 px-4 py-2">Accessibility preferences (high contrast, etc.)</td>
                      <td className="border border-gray-300 px-4 py-2">Persistent</td>
                      <td className="border border-gray-300 px-4 py-2">Functional</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-mono text-sm">ph_*</td>
                      <td className="border border-gray-300 px-4 py-2">PostHog analytics</td>
                      <td className="border border-gray-300 px-4 py-2">1 year</td>
                      <td className="border border-gray-300 px-4 py-2">Analytics</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Third-Party Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Third-Party Cookies</h2>
              <p className="mb-4">
                We use the following trusted third-party services that may set cookies:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stripe:</strong> Secure payment processing (Necessary)</li>
                <li><strong>Stripe:</strong> Secure payment processing (Necessary)</li>
                <li><strong>PostHog:</strong> Website usage analytics (Analytics — Optional)</li>
                <li><strong>IONOS SMTP:</strong> Email delivery service (Necessary)</li>
              </ul>
            </section>

            {/* How to Control Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How to Control Cookies</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Browser Settings</h3>
              <p className="mb-4">
                Most browsers allow you to control cookies through settings:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
              </ul>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <p className="font-semibold mb-2">⚠️ Warning</p>
                <p>
                  Blocking all cookies may prevent you from using certain features of our website, including logging in and saving preferences.
                </p>
              </div>
            </section>

            {/* Do Not Track */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Do Not Track Signals</h2>
              <p>
                We respect Do Not Track (DNT) browser settings. When DNT is enabled, we will not track your activity across websites.
              </p>
            </section>

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-xl p-8">
              <h2 className="text-2xl font-bold mb-4">Questions About Cookies?</h2>
              <p className="mb-4">
                Contact our privacy team:
              </p>
              <div className="space-y-2">
                <p><strong>Email:</strong> privacy@pricepalcompare.com</p>
                <p><strong>Data Protection Officer:</strong> dpo@pricepalcompare.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
